import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CompanySelector } from '../domain/CompanySelector';
import { useData } from '../../context/DataContext';
import { clearSessionKey } from '../../utils/session';
import { LayoutDashboardIcon, UsersIcon, FileTextIcon, RepeatIcon, SettingsIcon, DollarSignIcon, LandmarkIcon, MenuIcon, XIcon, TrendingUpIcon, PieChartIcon, BarChart3Icon, UsersRoundIcon, UserPlusIcon, ContactIcon, FilterIcon, FolderKanbanIcon, ClockIcon, ReceiptIcon, LogOutIcon } from 'lucide-react';
// Custom event for resetting Settings page to company list
export const SETTINGS_RESET_EVENT = 'settings-reset-to-list';
type ViewMode = 'sales' | 'banking' | 'crm' | 'projects' | 'cost' | 'bills';
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('sales');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    companies,
    selectedCompanyId,
    setSelectedCompanyId
  } = useData();
  const handleLogout = () => {
    clearSessionKey();
    navigate('/login');
  };
  // Handle Settings navigation click - always reset to company list
  const handleSettingsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    // Always dispatch the reset event
    window.dispatchEvent(new CustomEvent(SETTINGS_RESET_EVENT));
    // If not already on settings page, navigate to it
    if (location.pathname !== '/settings') {
      navigate('/settings');
    }
  };
  const handleViewModeToggle = () => {
    // Rotate through all 6 modes: sales → banking → crm → projects → cost → bills → sales
    const modes: ViewMode[] = ['sales', 'banking', 'crm', 'projects', 'cost', 'bills'];
    const currentIndex = modes.indexOf(viewMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    const newMode = modes[nextIndex];
    setViewMode(newMode);
    setMobileMenuOpen(false);
    // Navigate to appropriate page
    if (newMode === 'banking') {
      navigate('/cash-flow');
    } else if (newMode === 'crm') {
      navigate('/lead-flow');
    } else if (newMode === 'projects') {
      navigate('/projects');
    } else if (newMode === 'cost') {
      navigate('/cost-tracking');
    } else if (newMode === 'bills') {
      navigate('/bill-flow');
    } else {
      navigate('/');
    }
  };
  const salesNavItems = [{
    path: '/',
    label: 'Dashboard',
    icon: LayoutDashboardIcon
  }, {
    path: '/customers',
    label: 'Customers',
    icon: UsersIcon
  }, {
    path: '/invoices',
    label: 'Invoices',
    icon: FileTextIcon
  }, {
    path: '/subscriptions',
    label: 'Subscriptions',
    icon: RepeatIcon
  }, {
    path: '/settings',
    label: 'Settings',
    icon: SettingsIcon
  }];
  const bankingNavItems = [{
    path: '/cash-flow',
    label: 'Banking',
    icon: LandmarkIcon
  }, {
    path: '/job-profit',
    label: 'Job Profit',
    icon: TrendingUpIcon
  }, {
    path: '/net-profit',
    label: 'Net Profit',
    icon: PieChartIcon
  }, {
    path: '/balance-sheet',
    label: 'Balance Sheet',
    icon: BarChart3Icon
  }, {
    path: '/high-value-customers',
    label: 'High Value Customers',
    icon: UsersRoundIcon
  }];
  const crmNavItems = [{
    path: '/lead-flow',
    label: 'Contacts',
    icon: ContactIcon
  }, {
    path: '/funnel',
    label: 'Funnel',
    icon: FilterIcon
  }];
  const projectNavItems = [{
    path: '/projects',
    label: 'Projects',
    icon: FolderKanbanIcon
  }];
  const costNavItems = [{
    path: '/cost-tracking',
    label: 'Time Tracking',
    icon: ClockIcon
  }];
  const billNavItems = [{
    path: '/bill-flow',
    label: 'Bills',
    icon: ReceiptIcon
  }];
  const navItems = viewMode === 'sales' ? salesNavItems : viewMode === 'banking' ? bankingNavItems : viewMode === 'crm' ? crmNavItems : viewMode === 'projects' ? projectNavItems : viewMode === 'cost' ? costNavItems : billNavItems;
  const getModeName = () => {
    if (viewMode === 'sales') return 'Sales Flow';
    if (viewMode === 'banking') return 'Cash Flow';
    if (viewMode === 'crm') return 'Lead Flow';
    if (viewMode === 'projects') return 'Project Flow';
    if (viewMode === 'cost') return 'Cost Flow';
    return 'Bill Flow';
  };
  const getModeIcon = () => {
    if (viewMode === 'sales') return <DollarSignIcon className="w-6 h-6 text-white" data-id="element-248" />;
    if (viewMode === 'banking') return <LandmarkIcon className="w-6 h-6 text-white" data-id="element-249" />;
    if (viewMode === 'crm') return <UserPlusIcon className="w-6 h-6 text-white" data-id="element-250" />;
    if (viewMode === 'projects') return <FolderKanbanIcon className="w-6 h-6 text-white" data-id="element-251" />;
    if (viewMode === 'cost') return <ClockIcon className="w-6 h-6 text-white" data-id="element-252" />;
    return <ReceiptIcon className="w-6 h-6 text-white" data-id="element-253" />;
  };
  const getModeColor = () => {
    if (viewMode === 'sales') return 'bg-blue-600';
    if (viewMode === 'banking') return 'bg-green-600';
    if (viewMode === 'crm') return 'bg-purple-600';
    if (viewMode === 'projects') return 'bg-orange-600';
    if (viewMode === 'cost') return 'bg-pink-600';
    return 'bg-red-600';
  };
  const getNextModeName = () => {
    if (viewMode === 'sales') return 'Cash Flow';
    if (viewMode === 'banking') return 'Lead Flow';
    if (viewMode === 'crm') return 'Project Flow';
    if (viewMode === 'projects') return 'Cost Flow';
    if (viewMode === 'cost') return 'Bill Flow';
    return 'Sales Flow';
  };
  // Render nav item - special handling for Settings
  const renderNavItem = (item: {
    path: string;
    label: string;
    icon: any;
  }, isMobile: boolean = false) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    // Special handling for Settings link
    if (item.path === '/settings') {
      return <li key={item.path} data-id="element-254">
          <button onClick={handleSettingsClick} className={`flex items-center gap-3 w-full text-left ${isMobile ? 'px-4 py-3' : 'px-4 py-2.5'} rounded-lg transition-colors ${isActive ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-255">
            <Icon className="w-5 h-5" data-id="element-256" />
            <span data-id="element-257">{item.label}</span>
          </button>
        </li>;
    }
    return <li key={item.path} data-id="element-258">
        <Link to={item.path} onClick={isMobile ? () => setMobileMenuOpen(false) : undefined} className={`flex items-center gap-3 ${isMobile ? 'px-4 py-3' : 'px-4 py-2.5'} rounded-lg transition-colors ${isActive ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-259">
          <Icon className="w-5 h-5" data-id="element-260" />
          <span data-id="element-261">{item.label}</span>
        </Link>
      </li>;
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-262">
      {/* Mobile Header */}
      <header className="lg:hidden bg-white border-b border-gray-200 sticky top-0 z-50" data-id="element-263">
        <div className="flex items-center justify-between px-4 py-3" data-id="element-264">
          <button onClick={handleViewModeToggle} className="flex items-center gap-2 flex-shrink-0" data-id="element-265">
            <div className={`w-8 h-8 ${getModeColor()} rounded-lg flex items-center justify-center`} data-id="element-266">
              {getModeIcon()}
            </div>
            <span className="text-lg font-bold text-gray-900" data-id="element-267">
              {getModeName()}
            </span>
          </button>

          {/* Company selector in mobile header */}
          <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)} className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 max-w-[300px] truncate mx-2 flex-shrink" data-id="element-268">
            {companies.map(company => <option key={company.id} value={company.id} data-id="element-269">
                {company.name}
              </option>)}
          </select>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-gray-600 hover:text-gray-900 flex-shrink-0" data-id="element-270">
            {mobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-271" /> : <MenuIcon className="w-6 h-6" data-id="element-272" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && <div className="border-t border-gray-200 bg-white" data-id="element-273">
            <nav className="p-4" data-id="element-274">
              <ul className="space-y-1" data-id="element-275">
                {navItems.map(item => renderNavItem(item, true))}
                {/* Log Out Button */}
                <li data-id="element-276">
                  <button onClick={() => {
                setMobileMenuOpen(false);
                handleLogout();
              }} className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-red-600 hover:bg-red-50 w-full text-left" data-id="element-277">
                    <LogOutIcon className="w-5 h-5" data-id="element-278" />
                    <span data-id="element-279">Log Out</span>
                  </button>
                </li>
              </ul>
            </nav>
          </div>}
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-64 bg-white border-r border-gray-200 flex-col" data-id="element-280">
        {/* Logo / View Mode Toggle */}
        <div className="p-6 border-b border-gray-200" data-id="element-281">
          <button onClick={handleViewModeToggle} className="flex items-center gap-3 w-full hover:opacity-80 transition-opacity" data-id="element-282">
            <div className={`w-10 h-10 ${getModeColor()} rounded-xl flex items-center justify-center`} data-id="element-283">
              {getModeIcon()}
            </div>
            <div className="text-left" data-id="element-284">
              <h1 className="text-xl font-bold text-gray-900" data-id="element-285">
                {getModeName()}
              </h1>
              <p className="text-xs text-gray-500" data-id="element-286">
                Click to switch to {getNextModeName()}
              </p>
            </div>
          </button>
        </div>

        {/* Company Selector */}
        <div className="p-4 border-b border-gray-200" data-id="element-287">
          <CompanySelector data-id="element-288" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto" data-id="element-289">
          <ul className="space-y-1" data-id="element-290">
            {navItems.map(item => renderNavItem(item, false))}
            {/* Log Out Button */}
            <li data-id="element-291">
              <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-red-600 hover:bg-red-50 w-full text-left" data-id="element-292">
                <LogOutIcon className="w-5 h-5" data-id="element-293" />
                <span data-id="element-294">Log Out</span>
              </button>
            </li>
          </ul>
        </nav>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8" data-id="element-295">{children}</main>
    </div>;
}